<template>
  <b-form
    v-if="job"
    enctype="multipart/form-data"
    class="pay-form form"
    @submit.prevent="onSubmit"
  >
    <div class="form__section">
      <h3 class="page-subtitle">Your details</h3>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              :value="user && user.full_name"
              type="text"
              readonly
              placeholder="Full Name"
            ></custom-input>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <div class="form__section payment-method">
      <div class="d-flex justify-content-between">
        <h3 class="page-subtitle">Payment Method</h3>
        <div>
          <b-button
            v-if="paymentMethods.length > 0"
            class="edit-btn btn--xs p-0 border-0"
            variant="outline-transparent"
            @click="showPaymentList = true"
            >Edit</b-button
          >
        </div>
      </div>
      <b-row>
        <b-col v-if="loadingPaymentList">
          <label>Fetching your default payment method...</label>
        </b-col>
        <b-col v-else>
          <template v-if="paymentMethod">
            <b-list-group>
              <b-list-group-item class="list-group-item--default">
                <b-img
                  v-if="paymentMethod.brandIcon"
                  :src="paymentMethod.brandIcon"
                  width="40"
                  height="24"
                  class="rounded-0 mr-3"
                ></b-img>
                <b-img
                  v-else
                  class="rounded-0 mr-3"
                  v-bind="cardProps"
                  rounded
                  alt="Logo"
                ></b-img>
                <span class="payment-method-label">
                  <!-- eslint-disable-next-line -->
                  {{ paymentMethod.card.brand | capitalize }} {{ `Ending in ${paymentMethod.card.last4}` }}
                </span>
                <div class="remove-link">
                  <b-link>
                    <b-icon scale="1.8" icon="check2"></b-icon>
                  </b-link>
                </div>
              </b-list-group-item>
            </b-list-group>
          </template>
          <template v-else>
            <payment-form ref="manualPaymentForm" :has-create="false" />
          </template>

          <error-message
            class="mb-2"
            sign=""
            :error="
              form.$getError('payment_method_id') || form.$getError('source')
            "
          ></error-message>
        </b-col>
      </b-row>
    </div>
    <div class="form__section">
      <h3 class="page-subtitle">Billing Information</h3>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.address_1"
              type="text"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('address_1')"
              placeholder="Address Line 1"
            ></custom-input>
            <error-message :error="form.$getError('address_1')"></error-message>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.city"
              type="text"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('city')"
              placeholder="City or Suburb"
            ></custom-input>
            <error-message :error="form.$getError('city')"></error-message>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.postal_code"
              type="number"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('postal_code')"
              placeholder="Postcode"
            ></custom-input>
            <error-message
              :error="form.$getError('postal_code')"
            ></error-message>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.state"
              type="text"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('state')"
              placeholder="State"
            ></custom-input>
            <error-message :error="form.$getError('state')"></error-message>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.country"
              type="text"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('country')"
              placeholder="Country"
            ></custom-input>
            <error-message :error="form.$getError('country')"></error-message>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <div class="form__section">
      <b-row>
        <b-col>
          <b-button
            type="submit"
            block
            variant="primary"
            :disabled="form.$busy || loadingPaymentList"
          >
            <b-spinner
              small
              v-if="form.$busy || loadingPaymentList"
            ></b-spinner>
            Confirm and pay</b-button
          >
        </b-col>
      </b-row>
    </div>

    <b-modal
      id="payment-list-modal"
      v-model="showPaymentList"
      centered
      hide-footer
      size="md"
      static
      title="Payment Methods"
      class="modal-page modal-form"
    >
      <b-img
        slot="modal-header-close"
        :src="require('@/assets/icons/x.svg')"
        width="45"
        height="30"
      ></b-img>
      <payment-list
        :for-selection="true"
        :has-edit="false"
        :has-create="false"
        :selected-payment-method="selectedPaymentMethod"
        @on-fetch="onPaymentFetched"
        @on-select="onPaymentChange"
      />

      <div class="form__section">
        <b-button
          class="submit-btn"
          variant="primary"
          @click="onPaymentMethodClicked"
          >Add Payment Method</b-button
        >
      </div>
    </b-modal>

    <b-modal
      id="payment-form-modal"
      v-model="showPaymentForm"
      title="Add Payment Method"
      centered
      hide-footer
      size="md"
      static
      class="modal-page modal-form"
    >
      <b-img
        slot="modal-header-close"
        :src="require('@/assets/icons/x.svg')"
        height="24"
      ></b-img>
      <payment-form
        ref="paymentForm"
        suffix="modal"
        @on-success="onPaymentCreation"
        @on-close="showPaymentForm = false"
      />
    </b-modal>

    <b-modal
      v-model="response"
      centered
      class="modal-confirmation text-center p-3"
      hide-footer
      static
      @hide="goBack"
    >
      <b-img
        slot="modal-header-close"
        :src="require('@/assets/icons/x-b.svg')"
        width="45"
        height="30"
      ></b-img>

      <div
        class="d-flex flex-column justify-content-between align-items-center text-center"
      >
        <h2>Transaction completed</h2>

        <b-img
          :src="require('@/assets/images/alert-success.svg')"
          width="150"
          height="150"
          class="modal-confirmation__icon"
        ></b-img>

        <p class="response-content my-4">
          Your payment is on hold. We will inform you once the offer has been
          accepted.
        </p>
        <div
          class="modal-confirmation__footer modal-confirmation__footer--single modal-confirmation__footer--normal"
        >
          <router-link :to="{ name: 'user.messages-page' }">
            <b-button variant="outline-primary">
              View my messages
            </b-button>
          </router-link>
        </div>
      </div>
    </b-modal>
  </b-form>
</template>
<script>
/**
 * ==================================================================================
 * Confirm and pay form
 * ==================================================================================
 **/

import { mapState, mapGetters, mapActions } from 'vuex'
import Job from '@/models/Job'
import Payment from '@/models/Payment'
import Form from '@/utils/form'
import PaymentForm from '@/components/forms/PaymentForm'
import CustomInput from '@/components/inputs/CustomInput'
import ErrorMessage from '@/components/inputs/ErrorMessage'
import PaymentList from '@/components/lists/PaymentList'

export default {
  components: {
    PaymentForm,
    CustomInput,
    ErrorMessage,
    PaymentList
  },
  props: {
    job: {
      type: [Job, Object]
    },
    startDate: {
      type: Date
    },
    endDate: {
      type: Date
    },
    price: {
      type: Object
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      paymentMethods: state => state.payment.list
    }),

    ...mapGetters({
      defaultPaymentMethod: 'payment/defaultPaymentMethod'
    }),

    paymentMethod() {
      return this.selectedPaymentMethod || this.defaultPaymentMethod
    }
  },
  watch: {
    user(newVal) {
      this.resetForm()
    }
  },
  data: function() {
    return {
      loadingPaymentList: true,
      showPaymentList: false,
      showPaymentForm: false,
      selectedPaymentMethod: null,
      transactionCode: null,
      response: false,
      form: new Form({
        address_1: null,
        city: null,
        postal_code: null,
        state: null,
        country: null
      }),
      cardProps: {
        blank: true,
        blankColor: '#00CE7D',
        width: 40,
        height: 24
      }
    }
  },

  methods: {
    ...mapActions({
      rentJob: 'job/rentJob',
      addAlert: 'alert/addAlert'
    }),

    async onSubmit() {
      if (this.form.$busy) return
      this.form.$setLoading(true)

      if (!this.paymentMethod) {
        await this.$refs.manualPaymentForm
          .onSubmit()
          .then(data => {
            this.rentSubmit(data.token.id)
          })
          .catch(() => {
            this.form.$setLoading(false)
          })
      } else {
        await this.rentSubmit()
      }
    },

    async rentSubmit(paymentToken = null) {
      this.form.$setLoading(true)

      await this.rentJob(this.getFormData(paymentToken))
        .then(data => {
          this.form.$setLoading(false)
          this.transactionCode = data.data.data.renter_code
          this.response = true
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)

          if (!this.paymentMethod) {
            this.$refs.manualPaymentForm.disable(false)
          }

          if (error.response.status === 401) {
            this.$router.replace({
              name: 'job.view-job-page',
              params: {
                id: this.job.permalink,
                title: this.job.name.replace(/ +/g, '-').toLowerCase(),
                alert: {
                  state: false,
                  label: 'You must be logged-in to rent a tool'
                }
              }
            })
          } else if (error.response.status !== 422) {
            this.$emit('recalculate', this.form.$errorMessage)
          }
        })
    },

    onPaymentFetched() {
      this.loadingPaymentList = false
    },

    onPaymentChange(payment) {
      this.showPaymentList = false
      this.selectedPaymentMethod = payment
    },

    onPaymentMethodClicked() {
      this.showPaymentList = false
      this.showPaymentForm = true
    },

    onPaymentCreation(payment) {
      this.showPaymentForm = false
      this.selectedPaymentMethod = new Payment(payment)
    },

    goBack() {
      this.$router.push({
        name: 'transaction.view-transaction-page',
        params: {
          id: this.transactionCode
        }
      })
    },

    getFormData(paymentToken = null) {
      let billing = this.form.$data()
      billing.full_name = this.user.full_name

      let form = {
        tool_id: this.job.id,
        from: this.parseDate(this.startDate, 'YYYY-MM-DD'),
        to: this.parseDate(this.endDate, 'YYYY-MM-DD'),
        billing: billing,
        price: this.price.tool_price,
        grand_total: this.price.grand_total
      }

      if (this.paymentMethod) {
        form.payment_method_id = this.paymentMethod.id
      } else {
        form.source = paymentToken
      }

      return form
    },

    resetForm() {
      this.transactionCode = null

      if (this.user) {
        this.form.address_1 = this.user.address_1 || null
        this.form.city = this.user.city || null
        this.form.postal_code = this.user.postal_code || null
        this.form.state = this.user.state || null
        this.form.country = this.user.country || null
      }
    }
  },

  created() {
    this.resetForm()
  }
}
</script>
<style lang="scss" scoped>
.pay-form /deep/ {
  .response-content {
    max-width: 400px;
  }

  .remove-link {
    position: absolute;
    top: 12.5px;
    right: 15px;
  }

  .payment-method {
    &-label {
      color: var(--brand-green);
    }
  }

  .submit-btn {
    width: 300px;
  }

  .list-group-item {
    border-radius: 3px;
    margin-bottom: 1.4rem;
    border: 1px solid var(--input-border-color);
  }

  .list-group-item--default {
    border: 1px solid var(--brand-green);
  }

  .modal-form {
    .modal-header {
      padding-bottom: 2rem;

      .close {
        margin-right: -25px;
      }
    }

    .modal-content {
      padding-bottom: 5px;
    }
  }
}
</style>
